import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function StatisticsPage({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    gamesPlayed: 0,
    gamesWon: 0,
    currentStreak: 0,
    maxStreak: 0,
    averageGuesses: 0,
    totalQuizCorrect: 0,
    totalQuizAttempts: 0,
    aminoAcidsLearned: 0,
    lastPlayedDate: null,
    guessDistribution: [0, 0, 0, 0, 0, 0], // guesses 1-6
    categoryStats: {
      attributes: { correct: 0, total: 0 },
      abbreviations: { correct: 0, total: 0 },
      images: { correct: 0, total: 0 }
    }
  });

  useEffect(() => {
    // Redirect to auth if not logged in
    if (!user) {
      navigate('/auth');
      return;
    }

    // Load user statistics from backend API first, fallback to localStorage
    const loadUserStats = async () => {
      try {
        // Try to fetch from backend first
        const response = await fetch(`${process.env.REACT_APP_API_URL}/progress/dashboard`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('aminole_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const backendData = await response.json();
          console.log('Backend stats loaded:', backendData);
          
          // Convert backend data to frontend format
          const backendStats = {
            gamesPlayed: backendData.totalGamesPlayed || 0,
            gamesWon: Math.round((backendData.totalGamesPlayed || 0) * (backendData.overallAccuracy || 0) / 100),
            currentStreak: 0, // Not tracked in backend yet
            maxStreak: 0, // Not tracked in backend yet
            averageGuesses: 0, // Not tracked in backend yet
            totalQuizCorrect: backendData.totalCorrectAnswers || 0,
            totalQuizAttempts: backendData.totalQuestionsAnswered || 0,
            aminoAcidsLearned: 0, // Not tracked in backend yet
            lastPlayedDate: backendData.recentSessions?.[0]?.completedAt || null,
            guessDistribution: [0, 0, 0, 0, 0, 0],
            categoryStats: {
              attributes: { correct: 0, total: 0 },
              abbreviations: { correct: 0, total: 0 },
              images: { correct: 0, total: 0 }
            }
          };
          
          setStats(backendStats);
        } else {
          // Fallback to localStorage
          const savedStats = localStorage.getItem(`aminole_stats_${user.id}`);
          if (savedStats) {
            setStats(JSON.parse(savedStats));
          }
        }
      } catch (error) {
        console.error('Error loading user stats:', error);
        // Fallback to localStorage
        try {
          const savedStats = localStorage.getItem(`aminole_stats_${user.id}`);
          if (savedStats) {
            setStats(JSON.parse(savedStats));
          }
        } catch (localError) {
          console.error('Error loading local stats:', localError);
        }
      }
    };

    loadUserStats();
  }, [user, navigate]);

  const winPercentage = stats.gamesPlayed > 0 ? Math.round((stats.gamesWon / stats.gamesPlayed) * 100) : 0;
  const quizAccuracy = stats.totalQuizAttempts > 0 ? Math.round((stats.totalQuizCorrect / stats.totalQuizAttempts) * 100) : 0;

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getStreakEmoji = (streak) => {
    if (streak >= 10) return '🔥';
    if (streak >= 5) return '⚡';
    if (streak >= 3) return '✨';
    return '📈';
  };

  const getAchievements = () => {
    const achievements = [];
    
    if (stats.gamesWon >= 1) achievements.push({ name: 'First Victory', emoji: '🎉', description: 'Won your first game' });
    if (stats.gamesWon >= 10) achievements.push({ name: 'Decathlon', emoji: '🏆', description: 'Won 10 games' });
    if (stats.currentStreak >= 5) achievements.push({ name: 'On Fire', emoji: '🔥', description: '5 game win streak' });
    if (quizAccuracy >= 80 && stats.totalQuizAttempts >= 20) achievements.push({ name: 'Quiz Master', emoji: '🧠', description: '80%+ quiz accuracy' });
    if (stats.aminoAcidsLearned >= 15) achievements.push({ name: 'Biochemist', emoji: '⚗️', description: 'Learned 15+ amino acids' });
    
    return achievements;
  };

  return (
    <div className="statistics-page">
      <div className="stats-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Game
        </button>
        <h1>📊 Your Statistics</h1>
        <p>Hello, {user.firstName}! Here's your Aminole progress.</p>
      </div>

      <div className="stats-grid">
        {/* Game Statistics */}
        <div className="stats-card">
          <h2>🎮 Game Statistics</h2>
          <div className="stats-row">
            <div className="stat-item">
              <span className="stat-number">{stats.gamesPlayed}</span>
              <span className="stat-label">Games Played</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{winPercentage}%</span>
              <span className="stat-label">Win Rate</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.currentStreak} {getStreakEmoji(stats.currentStreak)}</span>
              <span className="stat-label">Current Streak</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.maxStreak}</span>
              <span className="stat-label">Max Streak</span>
            </div>
          </div>
        </div>

        {/* Quiz Performance */}
        <div className="stats-card">
          <h2>🧠 Quiz Performance</h2>
          <div className="stats-row">
            <div className="stat-item">
              <span className="stat-number">{quizAccuracy}%</span>
              <span className="stat-label">Quiz Accuracy</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.totalQuizCorrect}</span>
              <span className="stat-label">Correct Answers</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.aminoAcidsLearned}</span>
              <span className="stat-label">Amino Acids Learned</span>
            </div>
          </div>
        </div>

        {/* Guess Distribution */}
        <div className="stats-card">
          <h2>📈 Guess Distribution</h2>
          <div className="guess-distribution">
            {stats.guessDistribution.map((count, index) => (
              <div key={index} className="guess-bar">
                <span className="guess-number">{index + 1}</span>
                <div className="bar-container">
                  <div 
                    className="bar-fill" 
                    style={{ 
                      width: stats.gamesWon > 0 ? `${(count / stats.gamesWon) * 100}%` : '0%' 
                    }}
                  ></div>
                </div>
                <span className="guess-count">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="stats-card">
          <h2>📚 Quiz Categories</h2>
          <div className="category-stats">
            {Object.entries(stats.categoryStats).map(([category, data]) => {
              const accuracy = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
              return (
                <div key={category} className="category-item">
                  <div className="category-header">
                    <span className="category-name">{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                    <span className="category-accuracy">{accuracy}%</span>
                  </div>
                  <div className="category-progress">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${accuracy}%` }}
                    ></div>
                  </div>
                  <span className="category-details">{data.correct}/{data.total} correct</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Achievements */}
        <div className="stats-card">
          <h2>🏆 Achievements</h2>
          <div className="achievements">
            {getAchievements().length > 0 ? (
              getAchievements().map((achievement, index) => (
                <div key={index} className="achievement-item">
                  <span className="achievement-emoji">{achievement.emoji}</span>
                  <div className="achievement-info">
                    <span className="achievement-name">{achievement.name}</span>
                    <span className="achievement-description">{achievement.description}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-achievements">Keep playing to unlock achievements! 🌟</p>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="stats-card">
          <h2>📅 Recent Activity</h2>
          <div className="activity-info">
            <p><strong>Last Played:</strong> {formatDate(stats.lastPlayedDate)}</p>
            <p><strong>Average Guesses:</strong> {stats.averageGuesses > 0 ? stats.averageGuesses.toFixed(1) : 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatisticsPage;
